<template>
  <form
    class="invoice-detail"
    @submit.prevent="onSubmit"
  >
    <div class="columns is-multiline">
      <div class="column is-6">
        <Receiver
          :is-loading="loading"
          :disabled="loading || stamped"
        />
      </div>

      <div class="column is-6">
        <VoucherDetails
          :disabled="loading || stamped"
          :is-loading="loading"
        />
      </div>

      <div
        v-if="voucherPaymentMethod === 'PPD'"
        class="column is-12"
      >
        <Payments />
      </div>

      <div class="column is-12">
        <Concepts
          v-if="!loading"
          :is-loading="loading"
          :disabled="loading || stamped"
        />
      </div>

      <div class="column is-12">
        <CfdisRelatedReplacement
          replacement-type="replacementInvoice"
          :disabled="loading || stamped"
        />
      </div>

      <div class="column is-12">
        <Comments
          :disabled="loading || stamped"
        />
      </div>

      <div class="column is-12 action-buttons">
        <div
          v-if="stamped || canceled"
          class="field is-grouped"
        >
          <p
            v-if="stamped && !canceled"
            class="control"
          >
            <a
              class="button is-info"
              :class="{ 'is-loading': loading }"
            >
              Factura Timbrada
            </a>
          </p>
          <p
            v-if="canceled"
            class="control"
          >
            <a
              class="button is-danger"
              :class="{ 'is-loading': loading }"
            >
              Factura Cancelada
            </a>
          </p>
          <p class="control">
            <RouterLink
              class="button"
              :class="{ 'is-loading': loading }"
              :disabled="loading"
              :to="{ name: 'InvoicesHistory' }"
            >
              Regresar
            </RouterLink>
          </p>
        </div>
        <div
          v-else
          class="field is-grouped"
        >
          <p class="control">
            <button
              class="button is-primary"
              :class="{ 'is-loading': loading }"
              :disabled="loading"
              @click="setStamp(false)"
            >
              Guardar
            </button>
          </p>
          <p class="control">
            <button
              class="button is-success"
              type="submit"
              :class="{ 'is-loading': loading }"
              :disabled="loading"
              @click="setStamp(true)"
            >
              Guardar y Timbrar
            </button>
          </p>
          <p class="control">
            <RouterLink
              class="button"
              :disabled="loading"
              :class="{ 'is-loading': loading }"
              :to="{ name: 'StampedList', query: { size: 20, page: 1 } }"
            >
              Regresar
            </RouterLink>
          </p>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import moment from 'moment';
import { mapState, mapMutations, mapActions } from 'vuex';

const View = {
  name: 'InvoiceDetail',
  components: {
    Receiver: () => import(/* webpackChunkName: "invoice-receiver" */ '@/components/Admin/Invoices/NewInvoice/Receiver.vue'),
    VoucherDetails: () => import(/* webpackChunkName: "invoice-voucher-details" */ '@/components/Admin/Invoices/NewInvoice/VoucherDetails.vue'),
    Concepts: () => import(/* webpackChunkName: "invoice-concepts" */ '@/components/Admin/Invoices/NewInvoice/Concepts.vue'),
    CfdisRelatedReplacement: () => import(/* webpackChunkName: "cfdis-related-replacement" */ '@/components/Admin/CFDI/CfdisRelatedReplacement.vue'),
    Comments: () => import(/* webpackChunkName: "cfdis-comments" */ '@/components/Admin/CFDI/Comments.vue'),
    Payments: () => import(/* webpackChunkName: "invoice-payments" */ '../../../components/Invoice/InvoicePayments.vue'),
  },
  data() {
    return {
      loading: false,
      stamp: false,
    };
  },
  computed: {
    ...mapState({
      stamped: state => state.invoice.operation.stamped,
      status: state => state.invoice.operation.status,
      voucherPaymentMethod: state => state.invoice.voucherPaymentMethod,
    }),
    canceled() {
      return [201, 202].includes(this.status);
    },
  },
  async beforeMount() {
    this.loading = true;

    await this.getInvoiceByUuid(this.$route.params.uuid);

    if (!this.stamped) {
      this.setValue({
        accessor: 'voucherDate',
        value: moment().format('YYYY-MM-DDTHH:mm'),
      });
    }
    this.loading = false;
  },
  methods: {
    ...mapMutations({
      setValue: 'invoice/setValue',
      setInitialState: 'invoice/setInitialState',
    }),
    ...mapActions({
      getInvoiceByUuid: 'invoice/getByUuid',
      updateInvoice: 'invoice/updateByUuid',
    }),
    setStamp(value) {
      this.stamp = value;
    },
    async onSubmit() {
      this.loading = true;

      const { stamp } = this;
      const { haveEfirma, id: companyId } = this.$store.state.company;
      const { uuid } = this.$route.params;

      try {
        await this.updateInvoice({
          uuid,
          stamp,
          isSaleNote: false,
        });

        const content = stamp ? 'Timbrado exitoso' : 'Guardado correctamente';
        const name = stamp ? 'InvoicesHistory' : 'PreInvoiceList';

        this.$store.commit('ui/setValue', {
          accessor: 'catalogs.sku',
          value: [],
        });

        this.loading = false;

        const to = { name };

        if (!haveEfirma && stamp) {
          this.$warning({ message: 'Necesitas cargar tu CSD para emitir la factura' });

          to.name = 'Company';
          to.params = { id: companyId };
          to.query = { stamp: uuid };
        } else {
          this.$success({ message: content });
        }

        this.$router.push(to);
      } catch (error) {
        this.$store.dispatch('addTemporalNotification', {
          type: 'danger',
          content: stamp
            ? error.message
            : 'Ocurrio un error al guardar.',
        });

        this.loading = false;
      }
    },
  },
};

export default View;
</script>

<style scoped>
.invoice-detail {
  padding: 1rem;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
}

@media screen and (min-width: 1088px){
  .invoice-detail:not(.concepts) {
    width: calc(100vw - 265px);
  }
}
</style>
